import _ from 'lodash';
import React, { useCallback, useEffect, useId, useState, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { AsyncSelect, LabeledAsyncSelect,  } from '@commonsku/styles';
import { WindowedMenuList, formatOptionLabel } from '../helpers';
import { oauth } from '../../utils';
import { getClientOptions as baseGetClientOptions } from '../../selectors/dropdowns';
import { createFinalizeLoadClientList } from '../../actions/client';

const getClientOptions = createSelector(
    baseGetClientOptions,
    s => s.map(v => ({ ...v, value: v.key, label: v.value })),
);

const AsyncClientSelect = (
    {
        label,
        name,
        value,
        onChange,
        autoFocus=false,
        openMenuOnFocus=false,
        defaultMenuIsOpen=false,
        onClickNewClient=null,
        maxAvailableHeight=null,
        labelStyle={},
        isDisabled=false,
        horizontal=false,
    }
) => {
    const dispatch = useDispatch();
    const client_options = useSelector(getClientOptions);
    const findOption = useCallback(
        (clientId) => _.find(client_options, v => v.value === clientId),
        [client_options]
    );
    const [selectedClient, setSelectedClient] = useState(findOption(value));
    const maxMenuHeight = maxAvailableHeight ? maxAvailableHeight - 40 - 38 - 2 : undefined;
    const SelectMenuList = React.useCallback((p) => {
        return (
            <>
                <WindowedMenuList {...p} height={55} />
                {onClickNewClient && <div
                    onClick={() => {
                        onClickNewClient(1);
                    }}
                    style={{
                        cursor: 'pointer',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        background: '#E1F7FA',
                        color: '#00A0B6',
                        bottom: 0,
                        position: 'sticky',
                        textAlign: 'center',
                        marginBottom: 0,
                        fontWeight: 'bold',
                    }}
                >+ New Client</div>}
            </>
        );
    }, [onClickNewClient]);
    const loadClients = useCallback((searchTerm, callback) => {
        const search = _.debounce(async (client_name) => {
            if (!client_name || (client_name || '').length < 2) {
                return [];
            }
            const { json } = await oauth('GET', 'company', {
                company_type: 'CLIENT',
                search: true,
                include_inactive: false,
                'max-results': 20,
                company_name: client_name.toLowerCase().trim(),
                order_by: 'latest_use',
                order_dir: 'DESC',
            });
            const options = json.companies.map(
                c => ({
                    ...c,
                    label: `${c.client_name}`.trim(),
                    value: c.client_id,
                })
            );
            if (callback) {
                callback(options);
            }
            dispatch(createFinalizeLoadClientList(json.companies));
        }, 400);
        search(searchTerm);
    }, []);
    let Select = AsyncSelect;
    let extraProps = {};
    const isHorizontal = horizontal && label;
    // Only use LabeledAsyncSelect if we have a label AND it's not horizontal
    if (label && !isHorizontal) {
        Select = LabeledAsyncSelect;
        extraProps = { labelStyle };
    }
    useEffect(() => {
        setSelectedClient(findOption(value));
    }, [value, findOption]);
    // Define the base select element without a label prop if we are handling it horizontally
    const selectElement = (
        <Select
            inPopup
            {...extraProps}
            autoFocus={autoFocus || defaultMenuIsOpen}
            openMenuOnFocus={openMenuOnFocus || defaultMenuIsOpen}
            defaultMenuIsOpen={defaultMenuIsOpen}
            maxMenuHeight={maxMenuHeight}
            onChange={e => {
                setSelectedClient(e);
                onChange(e);
            }}
            value={selectedClient}
            placeholder="Select Client..."
            label={!isHorizontal ? label : undefined} // Don't pass label to Select if horizontal
            name={name}
            components={{ MenuList: SelectMenuList }}
            loadOptions={loadClients}
            cacheOptions
            defaultOptions={client_options}
            isDisabled={isDisabled}
            formatOptionLabel={formatOptionLabel}
        />
    );
    if (isHorizontal) {
        return (
            <div className="field row" style={{ marginBottom: '1rem' }}>
                <div className="small-12 medium-4 columns" style={{ alignSelf: 'center' }}>
                    <label style={{ margin: 0, fontSize: 'initial', ...labelStyle }}>
                        {label}
                    </label>
                </div>
                <div className="small-12 medium-8 columns">
                    {selectElement}
                </div>
            </div>
        );
    }
    return selectElement;
};

export default AsyncClientSelect;